import { useCallback, useState } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  DomainSummaryResponse,
  PublishedPolicySnapshot,
  SubdomainBreakdownRow,
  Window,
} from "@rua-watch/shared";
import {
  useAuthedRawFetch,
  useDomainSubdomains,
  useDomainSummary,
  useSources,
  useTimeseries,
} from "../api/hooks";
import { ProgressionCard } from "../components/ProgressionCard";
import { TlsRptCard } from "../components/TlsRptCard";
import { WindowPicker } from "../components/WindowPicker";
import { Pager, usePagination } from "../components/Pager";
import { formatInt, formatPct, fromUnix } from "../lib/format";

const SOURCES_PAGE_SIZE = 25;
const REPORTS_PAGE_SIZE = 15;

export function DomainDetailRoute() {
  const { domain } = useParams({ strict: false }) as { domain: string };
  const { subdomain } = useSearch({ strict: false }) as { subdomain?: string };
  const navigate = useNavigate();
  const [window, setWindow] = useState<Window>("30d");
  const [hideForwards, setHideForwards] = useState(false);
  const ts = useTimeseries(domain, window, subdomain);
  const src = useSources(domain, window, subdomain);
  const summary = useDomainSummary(domain);
  const subdomains = useDomainSubdomains(domain, window);
  const rawFetch = useAuthedRawFetch();

  // Drill Sources + volume down to a single sending subdomain (header_from),
  // persisted in the URL so the view is shareable/back-button friendly.
  const setSubdomain = useCallback(
    (next: string | undefined) => {
      navigate({
        to: "/domains/$domain",
        params: { domain },
        search: next ? { subdomain: next } : {},
      });
    },
    [navigate, domain],
  );

  const allSources = src.data ?? [];
  const forwarderCount = allSources.filter((s) => s.kind === "forwarder").length;
  const visibleSources = hideForwards
    ? allSources.filter((s) => s.kind !== "forwarder")
    : allSources;
  const sourcesPage = usePagination(
    visibleSources,
    SOURCES_PAGE_SIZE,
    `${window}|${subdomain ?? ""}|${hideForwards}`,
  );

  // Raw XML downloads need the Authorization header, which a plain anchor
  // can't send. Fetch the bytes, build a blob URL, and click an
  // anchor synthetically.
  const downloadRawReport = useCallback(
    async (reportId: number, suggestedName: string) => {
      const res = await rawFetch(`/api/reports/${reportId}/raw`);
      if (!res.ok) {
        alert(`Download failed: ${res.status} ${res.statusText}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = suggestedName;
      a.click();
      URL.revokeObjectURL(url);
    },
    [rawFetch],
  );

  return (
    <div className= "space-y-6">
      <div className= "flex items-center justify-between">
        <div>
          <div className= "text-xs uppercase text-slate-500 dark:text-slate-400">Domain</div>
          <h1 className= "font-mono text-xl font-semibold text-slate-900 dark:text-slate-100">{domain}</h1>
        </div>
        <WindowPicker value={window} onChange={setWindow} />
      </div>

      {summary.data ? (
        <div className= "grid grid-cols-1 gap-4 md:grid-cols-2">
          <PolicyCard policy={summary.data.policy} />
          <ReportersCard reporters={summary.data.reporters} />
        </div>
      ) : null}

      <ProgressionCard domain={domain} />

      <TlsRptCard domain={domain} />

      {subdomains.data && subdomains.data.rows.length > 0 ? (
        <SubdomainBreakdownCard
          rootDomain={domain}
          rows={subdomains.data.rows}
          activeSubdomain={subdomain}
          onSelect={setSubdomain}
        />
      ) : null}

      {subdomain ? (
        <div className= "flex items-center justify-between gap-3 rounded-lg border border-indigo-200 dark:border-indigo-900/50 bg-indigo-50/60 dark:bg-indigo-900/15 px-4 py-2 text-sm">
          <span className= "text-indigo-900 dark:text-indigo-200">
            Filtering <span className= "font-medium">Sources</span> &{" "}
            <span className= "font-medium">Volume</span> by subdomain{" "}
            <span className= "font-mono">{subdomain}</span>
          </span>
          <button
            type= "button"
            onClick={() => setSubdomain(undefined)}
            className= "rounded border border-indigo-300 dark:border-indigo-800 px-2 py-1 text-xs font-medium text-indigo-800 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/30"
          >
            Clear filter
          </button>
        </div>
      ) : null}

      <div className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
        <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">
          Volume & alignment
          {subdomain ? <span className= "ml-2 font-mono text-xs text-slate-400 dark:text-slate-500">· {subdomain}</span> : null}
        </h2>
        <div className= "h-72">
          <ResponsiveContainer width= "100%" height= "100%">
            <LineChart data={ts.data ?? []}>
              <CartesianGrid strokeDasharray= "3" stroke= "#e2e8f0" />
              <XAxis dataKey= "day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type= "monotone" dataKey= "messages" stroke= "#0f172a" dot={false} name= "messages" />
              <Line type= "monotone" dataKey= "dmarcPass" stroke= "#16a34a" dot={false} name= "DMARC pass" />
              <Line type= "monotone" dataKey= "spfAligned" stroke= "#2563eb" dot={false} name= "SPF aligned" />
              <Line type= "monotone" dataKey= "dkimAligned" stroke= "#9333ea" dot={false} name= "DKIM aligned" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className= "overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className= "flex items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 px-4 py-2">
          <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Sources</h2>
          <div className= "flex items-center gap-3">
            {forwarderCount > 0 ? (
              <label className= "flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                <input
                  type= "checkbox"
                  checked={hideForwards}
                  onChange={(e) => setHideForwards(e.target.checked)}
                  className= "h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-700"
                />
                Hide forwards ({forwarderCount})
              </label>
            ) : null}
            <span className= "text-xs text-slate-500 dark:text-slate-400">
              {visibleSources.length}
              {hideForwards && allSources.length !== visibleSources.length
                ?` of ${allSources.length}`
                :""}{""}
              IPs
            </span>
          </div>
        </div>
        <table className= "min-w-full text-sm">
          <thead className= "bg-slate-50 dark:bg-slate-800/60 text-left text-slate-600 dark:text-slate-400">
            <tr>
              <th className= "px-4 py-2 font-medium">IP</th>
              <th className= "px-4 py-2 font-medium">Classification</th>
              <th className= "px-4 py-2 font-medium">PTR / AS</th>
              <th className= "px-4 py-2 font-medium">Country</th>
              <th className= "px-4 py-2 font-medium text-right">Messages</th>
              <th className= "px-4 py-2 font-medium text-right">DMARC</th>
              <th className= "px-4 py-2 font-medium text-right">DKIM</th>
              <th className= "px-4 py-2 font-medium text-right">SPF</th>
            </tr>
          </thead>
          <tbody className= "divide-y divide-slate-100 dark:divide-slate-800">
            {sourcesPage.pageRows.map((s) => (
              <tr key={s.ip}>
                <td className= "px-4 py-2 font-mono align-top">
                  <Link to= "/ips/$ip" params={{ ip: s.ip }} className= "text-slate-900 dark:text-slate-100 hover:underline">
                    {s.ip}
                  </Link>
                  <div className= "font-sans text-xs text-slate-500 dark:text-slate-400">
                    last seen {fromUnix(s.lastSeen)}
                  </div>
                </td>
                <td className= "px-4 py-2">
                  <div className= "flex flex-wrap items-center gap-1">
                    <span className= "rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs">{s.classification}</span>
                    {s.kind === "forwarder" ? <ForwardPill /> : null}
                    {s.blocklistedSources > 0 ? (
                      <BlocklistedPill count={s.blocklistedSources} />
                    ) : null}
                  </div>
                </td>
                <td className= "px-4 py-2 text-slate-600 dark:text-slate-400">
                  <div className= "truncate max-w-xs">{s.ptr ?? "—"}</div>
                  <div className= "text-xs text-slate-500 dark:text-slate-400">AS{s.asn ?? "?"} {s.asnOrg ?? ""}</div>
                </td>
                <td className= "px-4 py-2">{s.country ?? "—"}</td>
                <td className= "px-4 py-2 text-right tabular-nums">{formatInt(s.messages)}</td>
                <td className= "px-4 py-2 text-right tabular-nums">
                  <PassRate value={s.passRate} />
                </td>
                <td className= "px-4 py-2 text-right">
                  <div className= "tabular-nums">
                    <PassRate value={s.dkim.passRate} />
                  </div>
                  <div className= "text-xs text-slate-500 dark:text-slate-400 truncate max-w-[14rem] ml-auto">
                    {s.dkim.topSigner
                      ?`${s.dkim.topSigner.domain}${s.dkim.topSigner.selector ?` [${s.dkim.topSigner.selector}]` :""}`
                      :"—"}
                  </div>
                </td>
                <td className= "px-4 py-2 text-right">
                  <div className= "tabular-nums">
                    <PassRate value={s.spf.passRate} />
                  </div>
                  <div className= "text-xs text-slate-500 dark:text-slate-400 truncate max-w-[14rem] ml-auto">
                    {s.spf.topEnvelope ?? "—"}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager
          page={sourcesPage.page}
          pageCount={sourcesPage.pageCount}
          total={sourcesPage.total}
          from={sourcesPage.from}
          to={sourcesPage.to}
          onPage={sourcesPage.setPage}
          unit= "IPs"
        />
      </div>

      {summary.data ? (
        <RecentReportsTable
          reports={summary.data.recentReports}
          onDownload={downloadRawReport}
        />
      ) : null}
    </div>
  );
}

function PolicyCard({ policy }: { policy: PublishedPolicySnapshot | null }) {
  return (
    <div className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
      <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Policy published</h2>
      {!policy ? (
        <p className= "mt-2 text-sm text-slate-500 dark:text-slate-400">No reports received yet for this domain.</p>
      ) : (
        <dl className= "mt-2 grid grid-cols-3 gap-y-2 text-sm">
          <dt className= "text-slate-500 dark:text-slate-400">p</dt>
          <dd className= "col-span-2">
            <PolicyPill p={policy.p ?? "—"} />
          </dd>
          <dt className= "text-slate-500 dark:text-slate-400">sp</dt>
          <dd className= "col-span-2">{policy.sp ? <PolicyPill p={policy.sp} /> :"—"}</dd>
          <dt className= "text-slate-500 dark:text-slate-400">pct</dt>
          <dd className= "col-span-2">{policy.pct ?? 100}</dd>
          <dt className= "text-slate-500 dark:text-slate-400">Alignment</dt>
          <dd className= "col-span-2">
            DKIM: {policy.adkim ?? "r"} · SPF: {policy.aspf ?? "r"}
          </dd>
          <dt className= "text-slate-500 dark:text-slate-400">Observed in</dt>
          <dd className= "col-span-2 text-xs text-slate-500 dark:text-slate-400">
            most recent report (window starting {fromUnix(policy.observedAt)})
          </dd>
        </dl>
      )}
    </div>
  );
}

function PassRate({ value }: { value: number }) {
  const color =
    value >= 0.95 ?"text-emerald-700 dark:text-emerald-300" : value >= 0.5 ?"text-amber-700 dark:text-amber-300" :"text-red-700 dark:text-red-300";
  return <span className={color}>{formatPct(value)}</span>;
}

// Marks an IP listed in one or more DNSBLs at the last reputation
// sweep. Detail (which list, what category) is on the IpDetail page.
function BlocklistedPill({ count }: { count: number }) {
  return (
    <span
      title={`Listed in ${count} DNSBL${count === 1 ?"" :"s"} at last reputation check.`}
      className= "rounded bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-800 dark:text-red-300"
    >
      Blocklisted
    </span>
  );
}

// Marks an IP that matches the auto-forwarding signature: a mailbox
// provider's outbound IP carrying DKIM-aligned mail with SPF failing.
// See queries.ts::classifyKind for the heuristic.
function ForwardPill() {
  return (
    <span
      title= "Auto-forwarding pattern: mailbox provider relaying DKIM-signed mail (SPF fails because the forwarder isn't in original sender's SPF). Not a spoofing signal."
      className= "rounded bg-sky-100 dark:bg-sky-900/30 px-2 py-0.5 text-xs font-medium text-sky-800 dark:text-sky-300"
    >
      Forward
    </span>
  );
}

// Coloring reflects"how protected is the domain" —`reject` is the goal
// (full enforcement → green), `none` means no enforcement at all → red.
// This is the OPPOSITE of how`disposition` colors should read at the
// per-record level (where reject means a specific message was blocked).
function PolicyPill({ p }: { p: string }) {
  const color =
    p === "reject"
      ?"bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300"
      : p === "quarantine"
      ?"bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300"
      : p === "none"
      ?"bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
      :"bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400";
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${color}`}>{p}</span>;
}

// Per-subdomain breakdown for a root domain. Modern DMARC reports
// arrive scoped to the organizational domain but each record can
// carry a different`header_from` — this card splits those out so
// operators can see"cc.plusdin.com.br = 80% pass, news.plusdin.com.br
// 40% pass" instead of one rolled-up number that hides the bad apple.
function SubdomainBreakdownCard({
  rootDomain,
  rows,
  activeSubdomain,
  onSelect,
}: {
  rootDomain: string;
  rows: SubdomainBreakdownRow[];
  activeSubdomain?: string;
  onSelect: (subdomain: string | undefined) => void;
}) {
  return (
    <div className= "overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className= "flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-4 py-2">
        <div>
          <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Subdomain breakdown</h2>
          <p className= "text-xs text-slate-500 dark:text-slate-400">
            One row per <code>header_from</code> seen in reports for{""}
            <span className= "font-mono">{rootDomain}</span> in this window. Click one to filter Sources &
            Volume.
          </p>
        </div>
        <span className= "text-xs text-slate-500 dark:text-slate-400">{rows.length}</span>
      </div>
      <table className= "min-w-full text-sm">
        <thead className= "bg-slate-50 dark:bg-slate-800/60 text-left text-slate-600 dark:text-slate-400">
          <tr>
            <th className= "px-4 py-2 font-medium">Subdomain</th>
            <th className= "px-4 py-2 font-medium text-right">Messages</th>
            <th className= "px-4 py-2 font-medium text-right">DMARC</th>
            <th className= "px-4 py-2 font-medium text-right">DKIM</th>
            <th className= "px-4 py-2 font-medium text-right">SPF</th>
            <th className= "px-4 py-2 font-medium">Top source</th>
            <th className= "px-4 py-2 font-medium text-right">IPs</th>
            <th className= "px-4 py-2 font-medium">Last seen</th>
          </tr>
        </thead>
        <tbody className= "divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((s) => {
            const isRoot = s.headerFrom === rootDomain;
            const label = s.headerFrom ||"(no header_from) ";
            const selectable = !!s.headerFrom;
            const active = selectable && s.headerFrom === activeSubdomain;
            return (
              <tr
                key={s.headerFrom ||"__empty__"}
                className={
                  active
                    ?"bg-indigo-50 dark:bg-indigo-900/20"
                    : selectable
                    ?"cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40"
                    :""
                }
                onClick={selectable ? () => onSelect(active ? undefined : s.headerFrom) : undefined}
                title={selectable ? (active ?"Clear filter" :`Filter Sources & Volume by ${s.headerFrom}`) : undefined}
              >
                <td className= "px-4 py-2 font-mono">
                  <span className={active ?"font-medium text-indigo-800 dark:text-indigo-200" :""}>{label}</span>
                  {isRoot ? (
                    <span
                      className= "ml-2 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-600 dark:text-slate-400"
                      title= "Mail sent from the root domain itself"
                    >
                      root
                    </span>
                  ) : null}
                  {active ? (
                    <span className= "ml-2 rounded bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
                      filtering
                    </span>
                  ) : null}
                </td>
                <td className= "px-4 py-2 text-right tabular-nums">{formatInt(s.messages)}</td>
                <td className= "px-4 py-2 text-right tabular-nums">
                  <PassRate value={s.passRate} />
                </td>
                <td className= "px-4 py-2 text-right tabular-nums">
                  <PassRate value={s.dkimPassRate} />
                </td>
                <td className= "px-4 py-2 text-right tabular-nums">
                  <PassRate value={s.spfPassRate} />
                </td>
                <td className= "px-4 py-2 font-mono text-xs">
                  {s.topSourceIp ? (
                    <Link
                      to= "/ips/$ip"
                      params={{ ip: s.topSourceIp }}
                      onClick={(e) => e.stopPropagation()}
                      className= "text-slate-700 dark:text-slate-300 hover:underline"
                    >
                      {s.topSourceIp}
                    </Link>
                  ) : (
                    <span className= "text-slate-400 dark:text-slate-500">—</span>
                  )}
                </td>
                <td className= "px-4 py-2 text-right tabular-nums">
                  {formatInt(s.uniqueSourceIps)}
                </td>
                <td className= "px-4 py-2 text-xs text-slate-600 dark:text-slate-400">{fromUnix(s.lastSeen)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReportersCard({ reporters }: { reporters: DomainSummaryResponse["reporters"] }) {
  return (
    <div className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
      <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Reporting orgs</h2>
      {reporters.length === 0 ? (
        <p className= "mt-2 text-sm text-slate-500 dark:text-slate-400">No reporters yet.</p>
      ) : (
        <ul className= "mt-2 space-y-1 text-sm">
          {reporters.slice(0, 8).map((r) => (
            <li key={r.orgName} className= "flex items-center justify-between gap-3">
              <span className= "truncate">{r.orgName}</span>
              <span className= "flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                <span className= "tabular-nums">{formatInt(r.reportCount)}</span>
                <span>{fromUnix(r.lastSeenAt)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RecentReportsTable({
  reports,
  onDownload,
}: {
  reports: DomainSummaryResponse["recentReports"];
  onDownload: (reportId: number, filename: string) => void;
}) {
  const page = usePagination(reports, REPORTS_PAGE_SIZE, reports.length);
  return (
    <div className= "overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className= "flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-4 py-2">
        <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Recent reports</h2>
        <span className= "text-xs text-slate-500 dark:text-slate-400">{reports.length}</span>
      </div>
      {reports.length === 0 ? (
        <p className= "px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">No reports yet.</p>
      ) : (
        <>
        <div className= "overflow-x-auto">
          <table className= "min-w-full text-sm">
            <thead className= "bg-slate-50 dark:bg-slate-800/60 text-left text-slate-600 dark:text-slate-400">
              <tr>
                <th className= "px-4 py-2 font-medium">Org</th>
                <th className= "px-4 py-2 font-medium">Report ID</th>
                <th className= "px-4 py-2 font-medium">Window</th>
                <th className= "px-4 py-2 font-medium">Received</th>
                <th className= "px-4 py-2 font-medium text-right">Messages</th>
                <th className= "px-4 py-2 font-medium text-right">Pass</th>
                <th className= "px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody className= "divide-y divide-slate-100 dark:divide-slate-800">
              {page.pageRows.map((r) => {
                const passRate = r.messageCount > 0 ? r.passCount / r.messageCount : 0;
                return (
                  <tr key={r.id}>
                    <td className= "px-4 py-2">{r.orgName}</td>
                    <td className= "px-4 py-2 font-mono text-xs text-slate-600 dark:text-slate-400 max-w-xs truncate">
                      {r.reportId}
                    </td>
                    <td className= "px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                      {fromUnix(r.dateStart)} →
                      <br />
                      {fromUnix(r.dateEnd)}
                    </td>
                    <td className= "px-4 py-2 text-xs text-slate-600 dark:text-slate-400">{fromUnix(r.receivedAt)}</td>
                    <td className= "px-4 py-2 text-right tabular-nums">{formatInt(r.messageCount)}</td>
                    <td className= "px-4 py-2 text-right tabular-nums">
                      <span
                        className={
                          r.messageCount === 0
                            ?"text-slate-400 dark:text-slate-500"
                            : passRate >= 0.95
                            ?"text-emerald-700 dark:text-emerald-300"
                            : passRate >= 0.5
                            ?"text-amber-700 dark:text-amber-300"
                            :"text-red-700 dark:text-red-300"
                        }
                      >
                        {r.messageCount > 0 ? formatPct(passRate) :"—"}
                      </span>
                    </td>
                    <td className= "px-4 py-2 text-right">
                      <div className= "inline-flex gap-1">
                        <Link
                          to= "/preview"
                          search={{ reportId: r.id }}
                          title= "Open this report in the parser preview"
                          className= "rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          Preview
                        </Link>
                        <button
                          onClick={() => onDownload(r.id, `${r.orgName}-${r.reportId}.xml`)}
                          title= "Download the raw XML"
                          className= "rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          XML
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pager
          page={page.page}
          pageCount={page.pageCount}
          total={page.total}
          from={page.from}
          to={page.to}
          onPage={page.setPage}
          unit= "reports"
        />
        </>
      )}
    </div>
  );
}
